import { useGetParameterOptions } from 'api/hooks/useGetParameterOptions/useGetParameterOptions'
import { type ProblemDetails } from 'api/types/http-problem-details'

import { useParameterOptionsRequest } from './useParameterOptionsRequest'
import {
  type AutofillRequestType,
  type UseGetParameterOptionsResponse,
  type UseParameterOptionsProps
} from './useParameterOptionsTypes'

type UseParameterOptionsAutofillProps<T extends AutofillRequestType> =
  UseParameterOptionsProps & { requestType: T }

/**
 *
 * Gets the lookupDependencies from parameter.autofill and sends a lookup request
 * to /autofill.
 * Setting the request type to autofill returns a string[][] for data grids.
 * Setting the request type to autofill-nested returns a nested response
 * for the NestedDataPicker
 */
export const useParameterOptionsAutofill = <T extends AutofillRequestType>({
  componentMetaData,
  parameter,
  isEnabled = false,
  requestType
}: UseParameterOptionsAutofillProps<T>): UseGetParameterOptionsResponse<T> => {
  const request = useParameterOptionsRequest(
    isEnabled,
    componentMetaData,
    parameter,
    requestType
  )
  const {
    isLoading,
    isRefetching,
    isSuccess,
    isError,
    error: unknownError,
    data: lookupResponseData,
    refetch
  } = useGetParameterOptions(request)

  const error = unknownError as ProblemDetails

  const defaultEmptyResponse: UseGetParameterOptionsResponse<AutofillRequestType> =
    {
      data: [],
      isLoading,
      isRefetching,
      isSuccess,
      isError,
      error,
      refetch
    }
  if (lookupResponseData) {
    const response: UseGetParameterOptionsResponse<AutofillRequestType> = {
      data: lookupResponseData,
      helperData: undefined,
      isLoading,
      isRefetching,
      isSuccess,
      isError,
      error,
      refetch
    }
    return response
  }

  return defaultEmptyResponse
}
